
import React from "react";
import { Form } from "@/components/dashboard/shops/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { FormsList } from "./FormsList";
import { EmptyState, FormContent } from "./index";

interface FormTabsContentProps {
  activeTab: string;
  setActiveTab: (value: string) => void;
  isEditing: boolean;
  isSaving: boolean;
  ctaButtonText: string;
  setCtaButtonText: (value: string) => void;
  formName: string;
  setFormName: (value: string) => void;
  formFields: any[];
  toggleFieldEnabled: (id: string) => void;
  toggleFieldRequired: (id: string) => void;
  deleteField: (id: string) => void;
  addNewField: () => void;
  updateFieldName: (id: string, name: string) => void;
  updateFieldType: (id: string, type: any) => void;
  handleSaveForm: () => Promise<any>;
  currentForm: Form | null;
  forms: Form[];
  hasFormTemplate: boolean;
  onSelectForm: (form: Form) => void;
  handleCreateNewForm: () => boolean;
  isLoading: boolean;
  formProductIds: string[];
  setFormProductIds: (ids: string[]) => void;
  handleCancelEdit: () => void;
  shopId: string;
  setIsDeleteDialogOpen: (value: boolean) => void;
}

export const FormTabsContent: React.FC<FormTabsContentProps> = ({
  activeTab,
  setActiveTab,
  isEditing,
  isSaving,
  ctaButtonText,
  setCtaButtonText,
  formName,
  setFormName,
  formFields,
  toggleFieldEnabled,
  toggleFieldRequired,
  deleteField,
  addNewField,
  updateFieldName,
  updateFieldType,
  handleSaveForm,
  currentForm,
  forms,
  hasFormTemplate,
  onSelectForm,
  handleCreateNewForm,
  isLoading,
  formProductIds,
  setFormProductIds,
  handleCancelEdit,
  shopId,
  setIsDeleteDialogOpen
}) => {
  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <div className="flex justify-between items-center mb-4">
        <TabsList className="grid grid-cols-2">
          <TabsTrigger value="forms">Mes formulaires</TabsTrigger>
          <TabsTrigger value="editor">Éditeur de formulaire</TabsTrigger>
        </TabsList>
        
        {activeTab === "editor" && currentForm && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setActiveTab("forms")}
            className="flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
        )}
      </div>
      
      <TabsContent value="forms">
        <FormsList 
          forms={forms}
          currentForm={currentForm}
          onSelectForm={onSelectForm}
          onCreateForm={handleCreateNewForm}
          isLoading={isLoading}
        />
      </TabsContent>
      
      <TabsContent value="editor">
        {!isEditing && !hasFormTemplate ? (
          <EmptyState onCreateForm={handleCreateNewForm} />
        ) : (
          <FormContent 
            isEditing={isEditing}
            isSaving={isSaving}
            ctaButtonText={ctaButtonText}
            setCtaButtonText={setCtaButtonText}
            formName={formName}
            setFormName={setFormName}
            formFields={formFields}
            toggleFieldEnabled={toggleFieldEnabled}
            toggleFieldRequired={toggleFieldRequired}
            deleteField={deleteField}
            addNewField={addNewField}
            updateFieldName={updateFieldName}
            updateFieldType={updateFieldType}
            handleSave={handleSaveForm}
            formOrganizationId={currentForm?.organization_id}
            formShopId={shopId}
            formProductIds={formProductIds}
            setFormProductIds={setFormProductIds}
            onDelete={() => setIsDeleteDialogOpen(true)}
            onCancel={handleCancelEdit}
          />
        )}
      </TabsContent>
    </Tabs>
  );
};
